import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { submitSelfSort } from '../api/user';
import { submitOrgFirmographics, OrgFirmographicsRequest, UserOrganizationAccess } from '../api/organization';
import { AccessTokenUser } from '../common/auth';

export type SelfSortSubmitPayload =
  | { variant: 'member'; jobRole: string }
  | {
      variant: 'owner';
      jobRole: string;
      orgId: number;
      firmographics: OrgFirmographicsRequest;
    }
  // A returning owner (role already collected once, globally) answering firmographics
  // for a NEW org: write only the per-org firmographics, never re-stamp the user.
  | {
      variant: 'ownerFirmographics';
      orgId: number;
      firmographics: OrgFirmographicsRequest;
    };

/**
 * Self-sort submit.
 *
 *   owner:             POST /organization/:id/firmographics ──► POST /user/self-sort
 *   ownerFirmographics: POST /organization/:id/firmographics
 *   member:                                                     POST /user/self-sort
 *
 * The role/completion write is always LAST, so a failed org write leaves the user
 * un-stamped and the banner correctly persists.
 *
 * On success we optimistically update the two authoritative, independent signals so
 * the banner disappears immediately without waiting for a refetch:
 *   - role completion → `selfSortCompletedAt` on the cached JWT user (per user),
 *   - org firmographics → `companySize` on the cached org (per org, flips the owner
 *     variant to member so the sidebar banner hides for THIS org only).
 * Then we invalidate to reconcile from the server.
 */
export const useSubmitSelfSortMutation = () => {
  return useMutation({
    mutationFn: async (payload: SelfSortSubmitPayload) => {
      if (payload.variant === 'owner' || payload.variant === 'ownerFirmographics') {
        await submitOrgFirmographics(payload.orgId, payload.firmographics);
      }
      if (payload.variant === 'owner' || payload.variant === 'member') {
        await submitSelfSort(payload.jobRole);
      }
    },
    onSuccess: (_data, payload) => {
      const wroteRole = payload.variant === 'owner' || payload.variant === 'member';
      const wroteFirmographics = payload.variant === 'owner' || payload.variant === 'ownerFirmographics';

      if (wroteRole) {
        queryClient.setQueryData<AccessTokenUser>(['userFromAccessToken'], (old) =>
          old ? { ...old, user: { ...old.user, selfSortCompletedAt: new Date().toISOString() } } : old
        );
        void queryClient.invalidateQueries({ queryKey: ['userFromAccessToken'] });
      }

      if (wroteFirmographics) {
        queryClient.setQueryData<UserOrganizationAccess[]>(['usersOrganizationAccess'], (old) =>
          old?.map((org) => (org.id === payload.orgId ? { ...org, ...payload.firmographics } : org))
        );
        void queryClient.invalidateQueries({ queryKey: ['usersOrganizationAccess'] });
      }
    },
  });
};
